import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpHelpersService, StorageService, UsersService } from '@coin/shared/core/data-access';
import { environment } from '@coin/shared/core/util-environments';
import { ObservableCachingHelper } from '@coin/shared/core/util-helpers';
import {
  Employee,
  EmployeeLight,
  EmployeeWithMaritalStatus,
  PaginatedResult,
  UserAuthReq,
  UserAuthStore
} from '@coin/shared/core/util-models';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { PermissionResource, StorageKey } from '@coin/shared/core/util-enums';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class GeneralUserService {
  private httpClient = inject(HttpClient);
  private authService = inject(AuthService);
  private httpHelper = inject(HttpHelpersService);
  private storageService = inject(StorageService);
  private userService = inject(UsersService);

  private cachingHelper = new ObservableCachingHelper<Employee>();

  public getUserByToken(forceLoggedInUser = false): Observable<UserAuthStore> {
    return forkJoin([
      from(this.authService.getAccessToken()),
      from(this.authService.getIdToken()),
      from(this.authService.getEmulation())
    ]).pipe(
      map(([accessToken, identityToken, emulationToken]) => ({
        accessToken: emulationToken && !forceLoggedInUser ? emulationToken : accessToken,
        identityToken: emulationToken && !forceLoggedInUser ? emulationToken : identityToken
      })),
      switchMap(tokens => {
        const storedInfo = this.storageService.get(StorageKey.LATEST_USER_INFO);
        if (
          storedInfo &&
          storedInfo.accessToken === tokens.accessToken &&
          storedInfo.identityToken === tokens.identityToken &&
          this.authService.getRemainingTime() > 0
        ) {
          return of(storedInfo);
        }

        const getUserInfo$ = this.httpClient.post<UserAuthReq>(`${environment.api.baseUrl}/user/info`, tokens).pipe(
          catchError(error => {
            this.authService.isAuthorized$.next(false);
            return throwError(() => error);
          })
        );
        const getAccessViews$ = this.userService.getUserAccessViewsUsers();

        return forkJoin([getUserInfo$, getAccessViews$]).pipe(
          map(([info, accessViews]) => ({ ...info, ...tokens, accessViews: accessViews as PermissionResource[] })),
          tap(userAuthStore => {
            this.storageService.set(StorageKey.LATEST_USER_INFO, userAuthStore);
          })
        );
      }),
      this.httpHelper.handleError('Error retrieving user')
    );
  }

  public getEmployeeDetailsByGid(gid: string): Observable<Employee> {
    return this.httpClient.get<Employee>(`${environment.api.baseUrl}/customer/api/v1/master/employees/gid/${gid}`).pipe(
      catchError((res: HttpErrorResponse) => {
        return throwError(`Error occured. ${res?.error?.reason || 'Unknown'} `);
      })
    );
  }

  public getEmployeeLightDetailsByGid(gid: string): Observable<EmployeeLight> {
    return this.httpClient
      .get<
        PaginatedResult<EmployeeLight>
      >(`${environment.api.baseUrl}/admin/api/v3/master/employees?Page=0&Size=1&Gid=${gid}`)
      .pipe(
        map(result => result?.content?.[0]),
        catchError((res: HttpErrorResponse) => {
          return throwError(`Error occured. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getGbrEmployeeDetailsByGid(gid: string): Observable<EmployeeWithMaritalStatus> {
    return this.httpClient
      .get<EmployeeWithMaritalStatus>(
        `${environment.api.baseUrl}/customer/api/v2/master/seasons/gbr/representatives/gid/${gid}`
      )
      .pipe(
        catchError((res: HttpErrorResponse) => {
          return throwError(`Error occured. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getEmployeeDetails(id: string): Observable<Employee> {
    return this.cachingHelper.cacheSetOnMiss$(
      this.httpClient.get<Employee>(`${environment.api.baseUrl}/admin/api/v3/master/employees/${id}`).pipe(
        catchError((res: HttpErrorResponse) => {
          return throwError(`Error occured. ${res?.error?.reason || 'Unknown'} `);
        })
      ),
      [id]
    );
  }

  public searchForEmployee(search: string, page = 1): Observable<PaginatedResult<Employee>> {
    const params = new HttpParams().append('Search', search).append('Pageable.Page', page);
    return this.httpClient
      .get<PaginatedResult<Employee>>(`${environment.api.baseUrl}/admin/api/v3/master/employees/query`, { params })
      .pipe(this.httpHelper.handleError('Could not get employees'));
  }
}
